"use client";

import * as stylex from "@stylexjs/stylex";
import { Moon, Sun } from "lucide-react";
import { useState } from "react";
import { copy } from "@/copy";
import type { Breakpoint } from "@/domain/breakpoint";
import { ambientLength, ambientMinutes, ambientSegments, ambientTime } from "@/domain/idle";
import type { AccountSettings, Ambient, AmbientSet } from "@/domain/schemas";
import { type Clock, formatClock, formatHour } from "@/domain/time";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { BackLink } from "@/ui/BackLink";
import { Button } from "@/ui/Button";
import { ColorBlock } from "@/ui/ColorBlock";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { PillTabs } from "@/ui/PillTabs";
import { TimeField } from "@/ui/TimeField";
import { TimePicker } from "@/ui/TimePicker";
import { settingsLayout } from "../layout";
type SetKey = "all" | "wknd";
type Edge = "s" | "e";
export type AmbientPick = `${SetKey}.${Edge}`;
type AmbientScheduleViewProps = {
  ambient: Ambient;
  brightness: Brightness;
  clock: Clock;
  idleTimeout: AccountSettings["idleTimeout"];
  breakpoint: Breakpoint;
  initialPick?: AmbientPick | undefined;
  onChange: (ambient: Ambient) => void;
  onBrightness: (brightness: Brightness) => void;
  onBack: () => void;
  onPreview: () => void;
};
type Brightness = AccountSettings["ambientBrightness"];
type ShownSet = {
  key: SetKey;
  title: string;
  heading: boolean;
};
const text = copy.ambientSchedule;
const EDGES = {
  s: "start",
  e: "end"
} as const;
const _temp = {
  kbCHJM: "xgq9j65",
  kzqmXN: "x5lhr3w",
  "$$css": true
};
const styles = {
  grid: {
    k1xSpc: "xrvj5dj",
    kOIVth: "x1ur044a",
    kGNEyG: "x7a106z",
    $$css: true
  },
  side: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1ur044a",
    $$css: true
  },
  segment: {
    kVAEAm: "x10l6tqk",
    k87sOh: "x13vifvy",
    krVfgx: "x1ey2m1c",
    kWkggS: "xiikbti",
    $$css: true
  },
  place: (left: number, width: number) => [_temp, {
    "--x-left": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${left}%`),
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${width}%`)
  }],
  preview: {
    kSGwAc: "xqcrz7y",
    kGVxlE: "xnfxhx2",
    $$css: true
  }
};
type SetFieldsProps = {
  shown: ShownSet;
  set: AmbientSet;
  clock: Clock;
  pick: AmbientPick | null;
  compact: boolean;
  onPick: (pick: AmbientPick | null) => void;
  onChange: (set: AmbientSet) => void;
};
type OverDayProps = Pick<AmbientScheduleViewProps, "ambient" | "clock" | "idleTimeout" | "onPreview">;
export function AmbientScheduleView({
  ambient,
  brightness,
  clock,
  idleTimeout,
  breakpoint,
  initialPick,
  onChange,
  onBrightness,
  onBack,
  onPreview
}: AmbientScheduleViewProps) {
  const [pick, setPick] = useState<AmbientPick | null>(initialPick ?? null);
  const changeMode = (mode: Ambient["mode"]) => {
    setPick(null);
    onChange({
      ...ambient,
      mode
    });
  };
  return <div {...stylex.props(styles.grid, settingsLayout(breakpoint))}>
      <BackLink label={text.back} onBack={onBack} />
      <div className="x78zum5 xdt5ytf x1rzw5jd xeuugli xh540kj xwe7p7i x16krsup x9f619">
        <Header />
        <div className="x78zum5 xdt5ytf x1v2ro7d">
          <Eyebrow>{text.schedule}</Eyebrow>
          <div className="x193iq5w x78zum5">
            <PillTabs label={text.schedule} options={text.modes} value={ambient.mode} onChange={changeMode} size="lg" />
          </div>
        </div>
        <BrightnessField value={brightness} onChange={onBrightness} />
        {shownSets(ambient.mode).map(shown => <SetFields key={shown.key} shown={shown} set={ambient[shown.key]} clock={clock} pick={pick} compact={breakpoint === "small"} onPick={setPick} onChange={set => onChange({
        ...ambient,
        [shown.key]: set
      })} />)}
        {ambient.mode === "off" && <StaysBright />}
      </div>
      <OverDay ambient={ambient} clock={clock} idleTimeout={idleTimeout} onPreview={onPreview} />
    </div>;
}
function shownSets(mode: Ambient["mode"]): ShownSet[] {
  if (mode === "off") return [];
  if (mode === "same") return [{
    key: "all",
    title: text.sets.every,
    heading: false
  }];
  return [{
    key: "all",
    title: text.sets.all,
    heading: true
  }, {
    key: "wknd",
    title: text.sets.wknd,
    heading: true
  }];
}
function rangeText(set: AmbientSet, clock: Clock): string {
  if (ambientLength(set) === 0) return text.notSet;
  const at = (time: string) => formatClock(ambientMinutes(time), clock);
  return text.range(at(set.start), at(set.end));
}
function barTicks(clock: Clock): [string, string][] {
  return [["noon", text.noon], ["evening", formatHour(18 * 60, clock)], ["midnight", text.midnight], ["morning", formatHour(6 * 60, clock)], ["next-noon", text.noon]];
}
function Header() {
  return <div className="x78zum5 xdt5ytf x152posx x1cy8zhl">
      <h2 className="x1ghz6dp x4c74qz x43273g xh92tn4 xju0f8q">{text.title}</h2>
      <span className="x11gisft x19d36u7 x15i68ds xfrs9s4 x1fzhlzt">{text.intro}</span>
    </div>;
}
function SetFields({
  shown,
  set,
  clock,
  pick,
  compact,
  onPick,
  onChange
}: SetFieldsProps) {
  const length = ambientLength(set);
  const open = pick?.startsWith(`${shown.key}.`) ? pick.slice(-1) as Edge : null;
  const toggle = (edge: Edge) => onPick(open === edge ? null : `${shown.key}.${edge}`);
  const fieldLabels = {
    noEnd: "",
    change: copy.events.time.change
  };
  return <fieldset aria-label={shown.title} className="xc342km x1717udv x1ghz6dp xeuugli x78zum5 xdt5ytf x1hu4l6k">
      {shown.heading && <Eyebrow>{shown.title}</Eyebrow>}
      <div className="xrvj5dj xnby9oq xddi9bj">
        <TimeField label={text.dims} value={ambientMinutes(set.start)} clock={clock} open={open === "s"} labels={fieldLabels} compact={compact} onToggle={() => toggle("s")} />
        <TimeField label={text.wakes} value={ambientMinutes(set.end)} clock={clock} {...length > 0 ? {
        length
      } : {
        error: text.sameTime
      }} open={open === "e"} labels={fieldLabels} compact={compact} onToggle={() => toggle("e")} />
      </div>
      {open && <TimePicker label={open === "s" ? text.dims : text.wakes} groupLabel={open === "s" ? text.dimGroup : text.wakeGroup} value={ambientMinutes(set[EDGES[open]])} clock={clock} labels={copy.events.time} minuteStep={15} doneVariant="primary" compact={compact} onChange={minutes => onChange({
      ...set,
      [EDGES[open]]: ambientTime(minutes)
    })} onDone={() => onPick(null)} />}
    </fieldset>;
}
function BrightnessField({
  value,
  onChange
}: {
  value: Brightness;
  onChange: (brightness: Brightness) => void;
}) {
  return <div className="x78zum5 xdt5ytf x1v2ro7d">
      <Eyebrow>{text.brightness}</Eyebrow>
      <div className="x193iq5w x78zum5">
        <PillTabs label={text.brightness} options={text.brightnessLevels} value={value} onChange={onChange} size="lg" />
      </div>
      <span className="x1j61zf2 x15i68ds xfrs9s4 x1fzhlzt">{text.brightnessBody}</span>
    </div>;
}
function StaysBright() {
  return <div className="x78zum5 x6s0dn4 xddi9bj x5r8ks9 x1fv5t7x x1qhigcl">
      <span className="x78zum5 x1okw0bk">
        <Icon icon={Sun} size={28} strokeWidth={1.5} />
      </span>
      <span className="x19d36u7 x15i68ds xfrs9s4 x1fzhlzt">{text.staysBright}</span>
    </div>;
}
function Strip({
  title,
  set,
  clock
}: {
  title: string;
  set: AmbientSet;
  clock: Clock;
}) {
  const range = rangeText(set, clock);
  return <div className="x78zum5 xdt5ytf x1v2ro7d">
      <div className="x78zum5 x1a02dak x1qughib x1pha0wt x26txhd">
        <span className="xwsyq91 xfbjfs5 x1yrajzq">{title}</span>
        <span className="x19d36u7 x15i68ds xss6m8b">{range}</span>
      </div>
      <div role="img" aria-label={text.bar(title, range)} className="x1n2onr6 x1vqgdyp xqhrb7l xh540kj xb3r6kr">
        {ambientSegments(set).map(({
        left,
        width
      }) => <div key={left} {...stylex.props(styles.segment, styles.place(left, width))} />)}
      </div>
      <div aria-hidden className="x78zum5 x1qughib xr0iwhn xhunfcg xkv8mnm xtvhhri">
        {barTicks(clock).map(([key, tick]) => <span key={key}>{tick}</span>)}
      </div>
    </div>;
}
function OverDay({
  ambient,
  clock,
  idleTimeout,
  onPreview
}: OverDayProps) {
  const sets = shownSets(ambient.mode);
  const off = ambient.mode === "off";
  return <ColorBlock tone="soft" padding="clamp(20px, 2.4vw, 32px)" xstyle={styles.side}>
      <Eyebrow>{text.over24}</Eyebrow>
      {off ? <span className="x19d36u7 x15i68ds xfrs9s4 x1fzhlzt">{text.noHours}</span> : <div className="x78zum5 xdt5ytf x1665zp3">
          {sets.map(shown => <Strip key={shown.key} title={shown.title} set={ambient[shown.key]} clock={clock} />)}
        </div>}
      <span className="x1j61zf2 x15i68ds xfrs9s4 x1fzhlzt">
        {off ? text.wakeLineOff : text.wakeLine(idleTimeout)}
      </span>
      <Button variant="secondary" icon={Moon} onClick={onPreview} xstyle={styles.preview}>
        {text.preview}
      </Button>
    </ColorBlock>;
}